/**
 * citation-resolver-http — live HTTP implementation of
 * :class:`ChipResolverPort` (THE-451 PR-3b).
 *
 * Replaces ``createCitationResolverFixture`` (which returns the same
 * ``verified``/``1.0`` verdict for every refKey) with a real wrapper
 * over ``POST /api/v1/citation-registry/resolve`` (theplaidoyer-api,
 * shipped 2026-05-27 via api#580 / THE-460).
 *
 * Why hand-rolled validation (no zod yet):
 *   The add-in deliberately stays zod-free per the auth-http precedent
 *   (``src/adapters/http/auth-http.ts`` header — "minimal hand-written
 *   fetch wrapper for now"). The BE wire is rich (full
 *   :class:`CitationRegistry` with articles + decisions + chips maps)
 *   but the port only needs four fields per refKey, so duck-typing
 *   covers the contract and the wire-shape drift surface is small.
 *   Adding zod is a separate decision (would also rewrite ``auth-http``
 *   for consistency).
 *
 * Status mapping (PR-3b only — no preflight signal here):
 *   - ``articles[refKey].verified === true`` → ``status: 'verified'``,
 *     ``score: 1.0``. Bonus: ``codeName`` + ``articleNumber`` extracted
 *     from the ``chips[refKey]`` ChipDescriptor when present.
 *   - ``decisions[refKey].verified === true`` → ``status: 'verified'``,
 *     ``score: 1.0``. Bonus: ``label`` synthesised from
 *     ``court_label · year n°number``.
 *   - Neither map carries the refKey, or carries it with
 *     ``verified === false`` → ``status: 'broken-anchor'``, ``score: 0``.
 *   - ``stale`` / ``unverified`` tiers are NOT reachable through
 *     ``/resolve``; they surface via ``/preflight`` in PR-3c.
 *
 * The adapter keeps the input order: ``verdicts[i].refKey ===
 * refKeys[i]`` for every i. Callers may rely on the index correspondence
 * (the integration test in ``src/adapters/office/__tests__/integration.test.ts``
 * does exactly that).
 */

import type {
  ChipResolverPort,
  ResolverVerdict,
} from '@/domain/citation-chip/ports'
import type { HttpErrorKind } from './_errors'

export interface CitationResolverHttpConfig {
  /** Backend base URL (no trailing slash). Mirrors ``createAuthHttp``. */
  baseUrl: string
  /** Lazy accessor for the bearer token. Lazy because the lawyer can
   *  log in / out mid-session — we read at call time, not at adapter
   *  construction. Returns ``null`` when the lawyer is signed out (the
   *  request still goes out but the BE will respond 401). */
  getAccessToken: () => string | null
  /** Fetch implementation override for tests. Defaults to the global. */
  fetch?: typeof fetch
}

interface ArticleEntryWire {
  verified: boolean
  // Other fields exist on the wire (title, code, code_label, etc.) but
  // PR-3b only consumes ``verified``. The chips map carries the display
  // bits we surface via ResolverVerdict.
}

interface DecisionEntryWire {
  verified: boolean
  courtLabel?: unknown
  year?: unknown
  number?: unknown
}

interface ChipDescriptorWire {
  codeName?: unknown
  articleNumber?: unknown
}

interface ResolveResponseWire {
  articles?: Record<string, ArticleEntryWire>
  decisions?: Record<string, DecisionEntryWire>
  chips?: Record<string, ChipDescriptorWire>
}

/**
 * Discriminated error class. Discriminates from the shared
 * ``HttpErrorKind`` union (``src/adapters/http/_errors.ts``) — THE-487
 * closure. The resolve endpoint never surfaces
 * ``'invalid_credentials'`` (that's a login-form failure mode, not a
 * resource-bearer failure) nor ``'rate_limited'`` today (no per-route
 * 429 quota); both remain valid vocabulary values for future tightening.
 */
class CitationResolverHttpError extends Error {
  constructor(
    public readonly kind: HttpErrorKind,
    message: string,
  ) {
    super(message)
    this.name = 'CitationResolverHttpError'
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Narrow + cast the JSON payload to ``ResolveResponseWire``.
 *
 *  We accept missing top-level maps (BE serialises ``{}`` for empty
 *  slices but a future BE could legitimately omit a key). We reject
 *  non-object payloads, primitive top-levels, and arrays. */
function parseResolveResponse(raw: unknown): ResolveResponseWire {
  if (!isPlainObject(raw)) {
    throw new CitationResolverHttpError(
      'malformed',
      'Resolve response is not a JSON object',
    )
  }
  const out: ResolveResponseWire = {}
  if (raw.articles !== undefined) {
    if (!isPlainObject(raw.articles)) {
      throw new CitationResolverHttpError(
        'malformed',
        'Resolve response: `articles` must be an object',
      )
    }
    out.articles = raw.articles as Record<string, ArticleEntryWire>
  }
  if (raw.decisions !== undefined) {
    if (!isPlainObject(raw.decisions)) {
      throw new CitationResolverHttpError(
        'malformed',
        'Resolve response: `decisions` must be an object',
      )
    }
    out.decisions = raw.decisions as Record<string, DecisionEntryWire>
  }
  if (raw.chips !== undefined) {
    if (!isPlainObject(raw.chips)) {
      throw new CitationResolverHttpError(
        'malformed',
        'Resolve response: `chips` must be an object',
      )
    }
    out.chips = raw.chips as Record<string, ChipDescriptorWire>
  }
  return out
}

function isVerifiedArticle(
  articles: Record<string, ArticleEntryWire> | undefined,
  refKey: string,
): boolean {
  const entry = articles?.[refKey]
  return isPlainObject(entry) && entry.verified === true
}

function isVerifiedDecision(
  decisions: Record<string, DecisionEntryWire> | undefined,
  refKey: string,
): boolean {
  const entry = decisions?.[refKey]
  return isPlainObject(entry) && entry.verified === true
}

function readStringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

function buildDecisionLabel(
  decisions: Record<string, DecisionEntryWire> | undefined,
  refKey: string,
): string | null {
  const entry = decisions?.[refKey]
  if (!isPlainObject(entry)) return null
  const court = readStringOrNull(entry.courtLabel)
  const year = typeof entry.year === 'number' ? entry.year : null
  const number = readStringOrNull(entry.number)
  if (!court && year === null && !number) return null
  const parts: string[] = []
  if (court) parts.push(court)
  if (year !== null) parts.push(String(year))
  if (number) parts.push(`n°${number}`)
  return parts.join(' · ') || null
}

function buildVerdict(
  refKey: string,
  wire: ResolveResponseWire,
): ResolverVerdict {
  if (isVerifiedArticle(wire.articles, refKey)) {
    const chip = wire.chips?.[refKey]
    return {
      refKey,
      status: 'verified',
      score: 1.0,
      codeName: isPlainObject(chip) ? readStringOrNull(chip.codeName) : null,
      articleNumber: isPlainObject(chip)
        ? readStringOrNull(chip.articleNumber)
        : null,
      label: null,
    }
  }
  if (isVerifiedDecision(wire.decisions, refKey)) {
    return {
      refKey,
      status: 'verified',
      score: 1.0,
      label: buildDecisionLabel(wire.decisions, refKey),
      codeName: null,
      articleNumber: null,
    }
  }
  // BE returned the refKey in articles/decisions with verified=false,
  // OR didn't return it at all (parser rejected before the lookup).
  // Either way: the chip cannot be verified. PR-3c will refine via
  // /preflight to distinguish "stale" from "broken-anchor".
  return {
    refKey,
    status: 'broken-anchor',
    score: 0,
    label: null,
    codeName: null,
    articleNumber: null,
  }
}

export function createCitationResolverHttp(
  config: CitationResolverHttpConfig,
): ChipResolverPort {
  const doFetch =
    config.fetch ?? ((input, init) => globalThis.fetch(input, init))
  const url = `${config.baseUrl}/api/v1/citation-registry/resolve`

  return {
    async resolve(refKeys: string[]): Promise<ResolverVerdict[]> {
      // BE rejects empty batches with 422; short-circuit so the lawyer
      // sees no spurious network call on a doc that hasn't been
      // annotated yet.
      if (refKeys.length === 0) return []

      const token = config.getAccessToken()
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }
      if (token) headers['Authorization'] = `Bearer ${token}`

      let res: Response
      try {
        res = await doFetch(url, {
          method: 'POST',
          headers,
          // Cookie fallback for the SaaS-style auth (third-party cookies
          // may be blocked in Office sandbox — see auth-http.ts header).
          credentials: 'include',
          body: JSON.stringify({ refKeys }),
        })
      } catch (err) {
        throw new CitationResolverHttpError(
          'network',
          err instanceof Error ? err.message : 'Réseau injoignable',
        )
      }

      if (res.status === 401) {
        throw new CitationResolverHttpError(
          'auth',
          'Session expirée — reconnectez-vous',
        )
      }
      if (!res.ok) {
        throw new CitationResolverHttpError(
          'server',
          `Resolve a échoué (HTTP ${res.status})`,
        )
      }

      let raw: unknown
      try {
        raw = await res.json()
      } catch (err) {
        throw new CitationResolverHttpError(
          'malformed',
          err instanceof Error
            ? `Réponse JSON invalide: ${err.message}`
            : 'Réponse JSON invalide',
        )
      }

      const wire = parseResolveResponse(raw)
      // Input order is part of the port contract — the integration test
      // pairs verdicts to refKeys by index.
      return refKeys.map((refKey) => buildVerdict(refKey, wire))
    },
  }
}

export { CitationResolverHttpError }
